import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import dayjs, { type Dayjs } from "utils/dayjs";
import {
  Alert,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Chip,
  DataRow,
  DatePicker,
  Drawer,
  FormField,
  Select,
  StatCard,
  TextField,
} from "@outpave/ds";
import type { ChipTone, SelectOption } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { MERCHANT_NAME_SEARCH } from "graphql/card";
import {
  PREVIEW_REWARD_PURCHASE,
  SEARCH_PLATFORM_COMPANIES,
} from "graphql/platform";
import { useSelectSearch } from "hooks/useSelectSearch";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { formatCashFull } from "utils/formatCash";
import type {
  RewardCategory,
  RewardPurchasePreview,
  RewardRuleOutcome,
} from "./types";

const OUTCOME: Record<RewardRuleOutcome, { label: string; tone: ChipTone }> = {
  WON: { label: "Pays", tone: "positive" },
  OUTRANKED: { label: "Outranked", tone: "neutral" },
  CAP_REACHED: { label: "Cap reached", tone: "warning" },
  CAP_CURRENCY: { label: "Cap in another currency", tone: "warning" },
};

type MatchOn = "CATEGORY" | "MERCHANT";

interface TestPurchaseDrawerProps {
  open: boolean;
  onClose: () => void;
  categories: RewardCategory[];
}

const TestPurchaseDrawer: React.FC<TestPurchaseDrawerProps> = ({
  open,
  onClose,
  categories,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [matchOn, setMatchOn] = useState<MatchOn>("CATEGORY");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [merchant, setMerchant] = useState<SelectOption | null>(null);
  const [amount, setAmount] = useState("");
  const [on, setOn] = useState<Dayjs | null>(dayjs());
  const [preview, { data, loading, error }] = useLazyQuery<{
    previewRewardPurchase: RewardPurchasePreview;
  }>(PREVIEW_REWARD_PURCHASE, { fetchPolicy: "network-only" });

  const companies = useSelectSearch<SelectOption>({
    skip: !open,
    fetchPage: async (query, after) => {
      const { data: page } = await client.query({
        query: SEARCH_PLATFORM_COMPANIES,
        variables: {
          searchTerm: query || undefined,
          first: DEFAULT_PAGINATION_SIZE,
          after,
        },
      });
      return {
        options: (page?.getCompanies?.edges ?? []).map(
          (edge: { node: { id: string; businessLegalName: string } }) => ({
            id: edge.node.id,
            label: edge.node.businessLegalName,
          }),
        ),
        hasNextPage: page?.getCompanies?.pageInfo?.hasNextPage,
        endCursor: page?.getCompanies?.pageInfo?.endCursor,
      };
    },
  });

  const merchants = useSelectSearch<SelectOption>({
    seed: merchant,
    skip: !open || matchOn !== "MERCHANT",
    fetchPage: async (query) => {
      if (!query.trim()) return { options: [] };
      const { data: found } = await client.query({
        query: MERCHANT_NAME_SEARCH,
        variables: { searchValue: query, searchType: "NAME" },
      });
      return {
        options: (found?.merchantNameSearch ?? []).map(
          (item: { id: string; name: string }) => ({
            id: item.id,
            label: item.name,
          }),
        ),
      };
    },
  });

  const value = Number(amount);
  const ready =
    Boolean(companyId) &&
    value > 0 &&
    (matchOn === "CATEGORY" ? Boolean(categoryId) : Boolean(merchant));

  const handleTest = () =>
    preview({
      variables: {
        input: {
          companyId,
          amount: value,
          ...(matchOn === "CATEGORY"
            ? { defaultCategoryId: categoryId }
            : { merchantId: merchant?.id }),
          ...(on && { on: on.format("YYYY-MM-DD") }),
        },
      },
    });

  const result = data?.previewRewardPurchase;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t("Simulate rules")}
      footer={
        <Inline gap="var(--space-3)" justify="end">
          <Button variant="primary-outlined" onClick={onClose}>
            {t("Close")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={handleTest}
            disabled={!ready || loading}
          >
            {t("Simulate")}
          </Button>
        </Inline>
      }
    >
      <Stack gap="var(--space-5)">
        <Typography variant="body2" color="text.secondary">
          {t("TestPurchaseCaption")}
        </Typography>
        <Select
          label={t("Account")}
          required
          searchable
          options={companies.options}
          loading={companies.loading}
          onInputChange={companies.onInputChange}
          onScrollEnd={companies.onScrollEnd}
          highlight={companies.query}
          value={companyId}
          onChange={setCompanyId}
        />
        <FormField label={t("Purchase at")} required asFieldset fullWidth>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={matchOn}
            onChange={(_, next) => next && setMatchOn(next as MatchOn)}
            aria-label={t("Purchase at")}
          >
            <ToggleButton value="CATEGORY">{t("Category")}</ToggleButton>
            <ToggleButton value="MERCHANT">{t("Merchant")}</ToggleButton>
          </ToggleButtonGroup>
        </FormField>
        {matchOn === "CATEGORY" ? (
          <Select
            label={t("Merchant category")}
            required
            searchable
            options={categories.map((category) => ({
              id: category.id,
              label: category.name,
            }))}
            value={categoryId}
            onChange={setCategoryId}
          />
        ) : (
          <Select
            label={t("Merchant")}
            required
            searchable
            options={merchants.options}
            loading={merchants.loading}
            onInputChange={merchants.onInputChange}
            highlight={merchants.query}
            noOptionsText={t("Type a merchant name to search")}
            value={merchant?.id ?? null}
            onChange={(id) =>
              setMerchant(id ? (merchants.optionById(id) ?? null) : null)
            }
          />
        )}
        <TextField
          label={t("Amount")}
          required
          type="number"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        <FormField label={t("Purchase date")}>
          <DatePicker
            aria-label={t("Purchase date")}
            value={on}
            onChange={setOn}
          />
        </FormField>
        {error && <Alert severity="error">{error.message}</Alert>}
        {result && (
          <Stack gap="var(--space-4)">
            <StatCard
              title={t("Reward")}
              value={formatCashFull(result.rewardAmount, result.currency)}
              caption={
                result.winner
                  ? t("RewardSimulationPaidBy", {
                      rule: result.winner.name,
                      rate: result.winner.ratePercent,
                    })
                  : t("TestPurchaseEarnsNothing")
              }
            />
            {result.candidates.length > 0 && (
              <Stack gap="var(--space-2)">
                <Typography
                  variant="subtitle2"
                  sx={{ paddingInline: "var(--space-4)" }}
                >
                  {t("Matching rules")}
                </Typography>
                {result.candidates.map((candidate) => (
                  <DataRow
                    key={candidate.rule.id}
                    type="amount"
                    title={candidate.rule.name}
                    subtitle={
                      candidate.outcome === "CAP_REACHED" &&
                      candidate.earnedThisMonth !== null
                        ? t("RewardCapEarnedThisMonth", {
                            earned: formatCashFull(
                              candidate.earnedThisMonth,
                              result.currency,
                            ),
                          })
                        : t("RewardRuleRatePriority", {
                            rate: candidate.rule.ratePercent,
                            priority: candidate.rule.priority,
                          })
                    }
                    trailing={
                      <Chip size="sm" tone={OUTCOME[candidate.outcome].tone}>
                        {t(OUTCOME[candidate.outcome].label)}
                      </Chip>
                    }
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
};

export default TestPurchaseDrawer;
